<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import InputSelect from '#components/elements/input/inputSelect/InputSelect.vue';
import type { InputSelectOptionType } from '#components/elements/input/inputSelect/InputSelectOption.vue';
import { useLanguage } from '#composables/useLanguage';
import {
  type CarrefourReferentialIntervalType,
  type ComparisonPeriodModeType,
  type ComparisonPeriodType,
  customDateToDate,
  customDateToDayJsDate,
  type DatePeriod,
  type DatePeriodIntervalType,
  type DateSelectionOption,
  dateToCustomDate,
  isNielsenInterval,
  type NielsenPeriodIntervalType,
  type Period,
  type PeriodIntervalType,
  type ProductTaxonomyType,
  type ReferentialIntervalsType
} from '@carrefour-gcs/shared';
import { computed, ref, watch } from 'vue';
import ComparisonPeriodSelector from './ComparisonPeriodSelector.vue';
import DateIntervalSelector from './DateIntervalSelector.vue';
import NielsenIntervalSelector from './nielsenIntervalSelector/NielsenIntervalSelector.vue';
import { getDefaultNielsenPeriod } from './nielsenPeriod';
import { type AvailablePeriodType, type SelectedIntervalType } from './periodSelector.model';
import {
  getCarrefourPeriodInterval,
  getCurrentDateReferentialInterval,
  getInitialComparisonInterval,
  isComparisonIntervalInvalid
} from './periodSelectorUtils';

const props = defineProps<{
  taxonomy: ProductTaxonomyType;
  selectedPeriod: DatePeriodIntervalType | NielsenPeriodIntervalType;
  comparisonPeriod: ComparisonPeriodType;
  referentialIntervals: ReferentialIntervalsType;
  carrefourReferentialIntervals: keyof CarrefourReferentialIntervalType;
  availablePeriods: AvailablePeriodType[];
  isDefaultValues: boolean;
  hasComparisonPeriod: boolean;
}>();

const emit = defineEmits<{
  change: [
    value: {
      period: DatePeriodIntervalType | NielsenPeriodIntervalType;
      comparisonPeriod: ComparisonPeriodType;
    }
  ];
  onClose: [];
}>();

const { t } = useLanguage();

const getDefaultValueInterval = (): DatePeriodIntervalType | NielsenPeriodIntervalType => {
  if (props.selectedPeriod.period === 'nielsen') {
    return props.selectedPeriod;
  }

  const dataReferentialInterval = getCurrentDateReferentialInterval({
    currentTaxonomy: props.taxonomy,
    referentialIntervals: props.referentialIntervals,
    selectedPeriodType: props.selectedPeriod.period,
    carrefourReferentialIntervals: props.carrefourReferentialIntervals
  });

  const interval = props.isDefaultValues
    ? getCarrefourPeriodInterval({
        selectedPeriod: props.selectedPeriod.period as DatePeriod,
        currentDateReferentialInterval: dataReferentialInterval,
        selectedInterval: props.selectedPeriod.periodInterval as PeriodIntervalType,
        selectedCalendarOption: props.selectedPeriod.option as DateSelectionOption
      })
    : {
        start: props.selectedPeriod.periodInterval.start,
        end: props.selectedPeriod.periodInterval.end
      };

  return {
    ...props.selectedPeriod,
    periodInterval: interval
  };
};
const periodIntervalInput = ref(getDefaultValueInterval());

const comparisonMode = ref<ComparisonPeriodModeType>(props.comparisonPeriod.comparisonMode);
const comparisonInterval = ref<PeriodIntervalType>(
  getInitialComparisonInterval({
    comparisonPeriod: props.comparisonPeriod,
    referencePeriodInterval: periodIntervalInput.value.periodInterval
  })
);

watch(
  () => props.comparisonPeriod,
  (newVal) => {
    comparisonMode.value = newVal.comparisonMode;
    if (newVal.comparisonMode === 'custom' && newVal.periodInterval && !isNielsenInterval(newVal.periodInterval)) {
      comparisonInterval.value = {
        start: newVal.periodInterval.start,
        end: newVal.periodInterval.end
      };
    }
  },
  { deep: true }
);

const comparisonSelectedInterval = computed({
  get: (): SelectedIntervalType => ({
    from: customDateToDate(comparisonInterval.value.start),
    to: customDateToDate(comparisonInterval.value.end)
  }),
  set: (val: SelectedIntervalType) => {
    comparisonInterval.value = {
      start: dateToCustomDate(val.from),
      end: dateToCustomDate(val.to)
    };
  }
});

const comparisonAvailableInterval = computed(() => {
  const dataReferentialInterval = getCurrentDateReferentialInterval({
    currentTaxonomy: props.taxonomy,
    referentialIntervals: props.referentialIntervals,
    selectedPeriodType: periodIntervalInput.value.period,
    carrefourReferentialIntervals: props.carrefourReferentialIntervals
  });

  const referenceEndDate = !isNielsenInterval(periodIntervalInput.value.periodInterval)
    ? customDateToDayJsDate(periodIntervalInput.value.periodInterval.end)
    : customDateToDayJsDate(dataReferentialInterval.end);

  return {
    start: customDateToDate(dataReferentialInterval.start),
    end: referenceEndDate.subtract(1, 'day').toDate()
  };
});

const isComparisonPeriodInvalid = computed(() => {
  if (!props.hasComparisonPeriod || comparisonMode.value !== 'custom') {
    return false;
  }

  return isComparisonIntervalInvalid({
    comparisonInterval: comparisonInterval.value,
    referencePeriodInterval: periodIntervalInput.value.periodInterval
  });
});

const referenceAvailableInterval = computed(() => {
  const dataReferentialInterval = getCurrentDateReferentialInterval({
    currentTaxonomy: props.taxonomy,
    referentialIntervals: props.referentialIntervals,
    selectedPeriodType: periodIntervalInput.value.period,
    carrefourReferentialIntervals: props.carrefourReferentialIntervals
  });

  const comparisonStartDate =
    props.hasComparisonPeriod && comparisonMode.value === 'custom' && comparisonInterval.value?.start
      ? customDateToDayJsDate(comparisonInterval.value.start)
      : undefined;

  const referentialStart = customDateToDate(dataReferentialInterval.start);

  return {
    start:
      comparisonStartDate && comparisonStartDate.toDate().getTime() > referentialStart.getTime()
        ? comparisonStartDate.toDate()
        : referentialStart,
    end: customDateToDate(dataReferentialInterval.end)
  };
});

const periodOptions = computed((): InputSelectOptionType<Period>[] => {
  return props.availablePeriods.map((availablePeriod) => {
    const hintKey = t(`timeSelectorPopin.period.hint.${availablePeriod.unavailableMessage}`);
    return {
      value: availablePeriod.period,
      label: t(`timeSelectorPopin.granularity.${availablePeriod.period}`),
      disabled: availablePeriod.unavailableMessage !== undefined,
      hint: availablePeriod.unavailableMessage ? t(hintKey) : undefined
    };
  });
});

const canPickPeriod = computed(() => periodOptions.value.length > 1);

const isDisabled = computed(() => {
  if (isComparisonPeriodInvalid.value) {
    return true;
  }

  // Disable apply button if selected period is daily and last_period option
  // is selected or if no option is selected

  return (
    (periodIntervalInput.value.period === 'daily' && periodIntervalInput.value.option === 'last_period') ||
    !periodIntervalInput.value.option
  );
});

const updatePeriodType = (periodType: Period | undefined) => {
  if (!periodType) {
    console.log('[DEBUG] TODO dont know how to handle this case yet', {});

    return;
  }

  // Switch from date period to nielsen
  if (periodType === 'nielsen') {
    if (!props.referentialIntervals.nielsen) {
      throw new Error('Nielsen referential interval is not defined');
    }
    periodIntervalInput.value =
      props.selectedPeriod.period === 'nielsen'
        ? props.selectedPeriod
        : getDefaultNielsenPeriod({
            nielsenInterval: props.referentialIntervals.nielsen.period,
            withOption: periodIntervalInput.value.option
          });
    return;
  }

  const interval = getCurrentDateReferentialInterval({
    currentTaxonomy: props.taxonomy,
    referentialIntervals: props.referentialIntervals,
    selectedPeriodType: periodType,
    carrefourReferentialIntervals: props.carrefourReferentialIntervals
  });

  periodIntervalInput.value = {
    period: periodType,
    option: 'dateRange',
    periodInterval: getCarrefourPeriodInterval({
      selectedPeriod: periodType as DatePeriod,
      currentDateReferentialInterval: interval,
      selectedInterval: periodIntervalInput.value.periodInterval as PeriodIntervalType,
      selectedCalendarOption: 'last_period'
    })
  };
};

const updatePeriodInterval = (value: DatePeriodIntervalType | NielsenPeriodIntervalType) => {
  periodIntervalInput.value = value;
};

const handleSelectedIntervalType = () => {
  if (!isDisabled.value) {
    emit('change', {
      period: periodIntervalInput.value,
      comparisonPeriod: {
        comparisonMode: comparisonMode.value,
        periodInterval:
          comparisonMode.value === 'custom'
            ? {
                start: comparisonInterval.value.start,
                end: comparisonInterval.value.end
              }
            : undefined
      }
    });
  }
};
</script>

<template>
  <div class="overflow-auto custom-vertical-scrollbar max-h-popupContent h-full px-5">
    <p class="py-4">
      {{ canPickPeriod ? t('timeSelectorPopin.description') : t('timeSelectorPopin.descriptionEssentialPackage') }}
    </p>
    <form class="flex flex-col items-center *:border-t last:*:border-t-0 *:py-4" @submit.prevent>
      <div class="w-full space-y-4">
        <span class="text-sm self-start">{{ t('timeSelectorPopin.subtitleGranularity') }}</span>

        <InputSelect
          v-if="canPickPeriod"
          :model-value="periodIntervalInput.period"
          name="periodSelector"
          label=""
          :options="periodOptions"
          :disabled="!canPickPeriod"
          test-id="period-type-selector"
          @update:model-value="updatePeriodType($event)"
        />

        <div
          v-else
          class="rounded border border-default-very-light text-default-disabled p-4 w-full mt-4 font-normal select-none"
        >
          {{ t(`timeSelectorPopin.granularity.${selectedPeriod.period}`) }}
        </div>
      </div>

      <div class="w-full">
        <span v-if="hasComparisonPeriod" class="text-lg font-bold mb-4">{{
          t('timeSelectorPopin.subtitleReferencePeriod')
        }}</span>
        <span v-else class="text-sm self-start">{{ t('timeSelectorPopin.subtitleInterval') }}</span>
        <DateIntervalSelector
          v-if="periodIntervalInput.period !== 'nielsen'"
          :current-taxonomy="taxonomy"
          :referential-intervals="referentialIntervals"
          :carrefour-referential-intervals="carrefourReferentialIntervals"
          :selected-period="periodIntervalInput"
          :available-interval="referenceAvailableInterval"
          @update-period="updatePeriodInterval($event)"
        />
        <NielsenIntervalSelector
          v-if="periodIntervalInput.period === 'nielsen' && referentialIntervals.nielsen"
          :referential-interval="referentialIntervals.nielsen.period"
          :selected-period="periodIntervalInput"
          @update-period="updatePeriodInterval($event)"
        />
      </div>

      <ComparisonPeriodSelector
        v-if="hasComparisonPeriod"
        :taxonomy="taxonomy"
        :referential-intervals="referentialIntervals"
        :carrefour-referential-intervals="carrefourReferentialIntervals"
        :reference-period="periodIntervalInput"
        :comparison-mode="comparisonMode"
        :interval="comparisonInterval"
        :selected-interval="comparisonSelectedInterval"
        :available-interval="comparisonAvailableInterval"
        :is-invalid="isComparisonPeriodInvalid"
        @update:comparison-mode="comparisonMode = $event"
        @update:interval="comparisonSelectedInterval = $event"
      />

      <div class="grid grid-cols-1 md:grid-cols-2 w-full gap-4">
        <ButtonBase size="large" variant="secondary" full-width @click="emit('onClose')">
          {{ t(`commonLabel.cancel`) }}
        </ButtonBase>

        <ButtonBase size="large" :disabled="isDisabled" full-width @click="handleSelectedIntervalType()">
          {{ t(`commonLabel.applySelection`) }}
        </ButtonBase>
      </div>
    </form>
  </div>
</template>
